import {
	CheckCircle,
	Circle,
	Clock,
	Edit,
	MessageCircle,
	Plus,
	Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getApplicableTestCases } from "../data/testCaseTemplates";
import { toChecklistItem } from "../lib/walletSnake";
import type { IntegrationStatus, ProductType, Wallet } from "../types";
import { requestOpenAIWidget } from "../utils/aiWidget";
import { notifications } from "../utils/notifications";
import ChecklistItemComponent from "./ChecklistItemComponent";
import { CelebrateIcon } from "./icons/CelebrateIcon";
import { WarningIcon } from "./icons/WarningIcon";
import ProductionWhitelistForm from "./ProductionWhitelistForm";
import { TestingAdminView } from "./TestingAdminView";
import { TestingSection } from "./TestingSection";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import WhitelistingForm from "./WhitelistingForm";

interface SlackChannelProps {
	value: string;
	onChange: (value: string) => void;
	onSave: () => void;
	onDelete: () => void;
}

interface TransactionProps {
	type: "Deposit" | "Withdraw";
	ref: string;
	showAdd: boolean;
	setType: (type: "Deposit" | "Withdraw") => void;
	setRef: (ref: string) => void;
	setShowAdd: (show: boolean) => void;
	onAdd: () => void;
	onDelete: (walletId: string, transactionId: string) => void;
}

interface ChecklistProps {
	onToggle: (key: string, item: unknown) => void;
	onWhitelistCheck: () => void;
	onStatusChange: (status: IntegrationStatus) => void;
	showProductionInfo: () => void;
	updateWallet: (id: string, updates: Record<string, unknown>) => void;
}

interface IntegrationWorkflowProps {
	wallet: Wallet;
	isUpdating?: boolean;
	onShowSuccess?: (message: string) => void;
	slackChannel: SlackChannelProps;
	transaction: TransactionProps;
	checklist: ChecklistProps;
	isPartnerView?: boolean;
	viewingStep?: IntegrationStatus;
}

const IntegrationWorkflow: React.FC<IntegrationWorkflowProps> = ({
	wallet,
	isUpdating = false,
	onShowSuccess,
	slackChannel: {
		value: slackChannelInput,
		onChange: setSlackChannelInput,
		onSave: handleSlackChannelSave,
		onDelete: handleDeleteSlackChannel,
	},
	transaction: {
		type: transactionType,
		ref: transactionRef,
		showAdd: showAddTransaction,
		setType: setTransactionType,
		setRef: setTransactionRef,
		setShowAdd: setShowAddTransaction,
		onAdd: handleAddTransaction,
		onDelete: deleteTransaction,
	},
	checklist: {
		onToggle: handleChecklistToggle,
		onWhitelistCheck: handleWhitelistCheck,
		onStatusChange: handleStatusChange,
		showProductionInfo: showProductionWhitelistInfo,
		updateWallet,
	},
	isPartnerView = false,
	viewingStep: viewingStepProp,
}) => {
	const [showProductionForm, setShowProductionForm] = useState(false);
	const { user } = useAuth();

	// Whitelisting form state
	const [showWhitelistForm, setShowWhitelistForm] = useState(false);

	// Parent owns viewing step; fallback to wallet status when not yet synced
	const viewingStep = viewingStepProp ?? wallet.integration_status;

	// Check if all required production information is provided
	const hasRequiredProductionInfo = () => {
		const custodialType =
			wallet.production_custodial_type || wallet.custodial_type;

		// Check Agent ID and Main Office ID (always required)
		if (!wallet.agent_id || !wallet.main_office_id) {
			return false;
		}

		// Check fields based on custodial type
		if (custodialType === "Custodial") {
			// Custodial requires: Home Domain, Funding Address, Deposit Address, Auth Address
			return !!(
				wallet.production_home_domain &&
				wallet.production_funding_address &&
				wallet.production_deposit_address &&
				wallet.production_auth_address
			);
		} else if (custodialType === "Non-Custodial") {
			// Non-Custodial requires: TOML File Domain
			return !!wallet.production_toml_file_domain;
		} else if (custodialType === "Both") {
			// Both requires all fields
			return !!(
				wallet.production_toml_file_domain &&
				wallet.production_home_domain &&
				wallet.production_funding_address &&
				wallet.production_deposit_address &&
				wallet.production_auth_address
			);
		}

		return false;
	};

	// One-time migration: Update legacy "Integrated" status to "Testing"
	React.useEffect(() => {
		if ((wallet.integration_status as string) === "Integrated") {
			updateWallet(wallet.id, { integrationStatus: "Testing" });
		}
	}, [wallet.id, wallet.integration_status, updateWallet]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleWhitelistSuccess = (_environments: string[]) => {
		// Update the wallet status and auto-advance to Step 2
		updateWallet(wallet.id, {
			isWhitelisted: true,
			whitelistEnvironments: ["Sandbox"], // Only show Sandbox
			integrationStatus: "Integrating", // Auto-advance to Step 2
		});

		setShowWhitelistForm(false); // Close the form after success
		onShowSuccess?.("Wallet successfully whitelisted!");
	};

	return (
		<>
			{/* Alt States - Admin only */}
			{!isPartnerView && (
				<div className="flex flex-col gap-4">
					<div className="flex gap-2">
						<Button
							type="button"
							variant={
								wallet.integration_status === "On-Hold" ? "default" : "outline"
							}
							size="sm"
							onClick={() =>
								wallet.is_whitelisted && handleStatusChange("On-Hold")
							}
							disabled={!wallet.is_whitelisted}
							title={
								wallet.integration_status === "On-Hold" ? "Resume" : "On-Hold"
							}
						>
							{wallet.integration_status === "On-Hold" ? "Resume" : "On-Hold"}
						</Button>
						<Button
							type="button"
							variant={
								wallet.integration_status === "Abandoned"
									? "destructive"
									: "outline"
							}
							size="sm"
							onClick={() =>
								wallet.is_whitelisted && handleStatusChange("Abandoned")
							}
							disabled={!wallet.is_whitelisted}
							title="Abandoned"
						>
							Abandoned
						</Button>
					</div>
				</div>
			)}

			<div className="flex flex-col gap-4 pt-2">
				{/* STEP 1: New - Pre-Integration */}
				{viewingStep === "New - Pre-Integration" && (
					<>
						<div>
							<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
								Step 1: Getting Started
							</h3>
							<p className="text-(--color-muted-foreground) text-sm">
								{isPartnerView ? (
									<>Get whitelisted to begin integration.</>
								) : (
									"The wallet needs to be whitelisted in Sandbox or Preview environments to begin integration."
								)}
							</p>
						</div>

						<div className="space-y-2">
							<div
								className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-all ${wallet.is_whitelisted ? "border-[#00E6C3]/35 bg-[#00E6C3]/5" : "border-(--color-border) bg-(--color-card) hover:border-(--color-accent-border)"}`}
							>
								<div className="flex items-center gap-3">
									{wallet.is_whitelisted ? (
										<CheckCircle
											size={18}
											className="text-[#00E6C3] shrink-0"
										/>
									) : (
										<Circle
											size={18}
											className="text-(--color-muted-foreground) shrink-0"
										/>
									)}
									<Label
										htmlFor="is-whitelisted"
										className="cursor-pointer font-medium text-sm text-(--color-foreground)"
									>
										Whitelist Wallet
										{wallet.is_whitelisted &&
											wallet.whitelist_environments &&
											wallet.whitelist_environments.length > 0 &&
											wallet.whitelist_environments.map((env) => (
												<Badge key={env} variant="info" className="ml-2">
													{env === "Sandbox" ? "Sandbox (Testnet)" : env}
												</Badge>
											))}
									</Label>
									<Checkbox
										id="is-whitelisted"
										checked={wallet.is_whitelisted}
										onCheckedChange={() => {
											if (!isPartnerView) {
												handleWhitelistCheck();
											}
										}}
										disabled={isPartnerView}
										className="hidden"
									/>
								</div>

								{/* Automated whitelisting (POSTs to /api/stellar/register) is
								    hidden for partners — whitelisting is handled manually via
								    ServiceNow for now. Admins keep the self-service form. */}
								{!isPartnerView && !wallet.is_whitelisted && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => setShowWhitelistForm(!showWhitelistForm)}
									>
										{showWhitelistForm ? "Hide Form" : "Get Whitelisted"}
									</Button>
								)}
							</div>

							{/* Inline Whitelisting Form */}
							{!isPartnerView && showWhitelistForm && (
								<WhitelistingForm
									wallet={wallet}
									isVisible={showWhitelistForm}
									onSuccess={handleWhitelistSuccess}
									onCancel={() => setShowWhitelistForm(false)}
								/>
							)}
						</div>
					</>
				)}

				{/* STEP 2: Integrating */}
				{viewingStep === "Integrating" && (
					<>
						<div>
							<div className="flex items-center justify-between mb-1">
								<h3 className="text-lg font-semibold text-(--color-foreground)">
									Step 2: Integration Phase
								</h3>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => requestOpenAIWidget()}
									title="Get help from AI assistant"
								>
									<MessageCircle size={16} />
									Ask me
								</Button>
							</div>
							{isPartnerView ? (
								<p className="text-(--color-muted-foreground) text-sm">
									Start building your integration. Check our{" "}
									<a
										href="https://developer.moneygram.com/moneygram-developer/docs/integrate-moneygram-ramps"
										target="_blank"
										rel="noopener noreferrer"
										className="text-(--color-accent) hover:underline font-medium"
									>
										Developer Portal
									</a>{" "}
									or chat with our AI dev assistant for guidance.
								</p>
							) : (
								<p className="text-(--color-muted-foreground) text-sm">
									Partner is actively building their integration. Set up
									communication and mark when complete.
								</p>
							)}
						</div>

						{!isPartnerView && (
							<>
								{/* Admin view: Full Slack functionality */}
								<div>
									<h4 className="text-sm font-semibold text-(--color-foreground) mb-1">
										Communication Channel
									</h4>
									<p className="text-(--color-muted-foreground) text-xs mb-2">
										Create a Slack channel (optional but recommended).
									</p>

									{!wallet.slack_channel_created?.completed ? (
										<div className="flex items-center gap-2">
											<Input
												value={slackChannelInput}
												onChange={(e) => setSlackChannelInput(e.target.value)}
												placeholder="e.g., #wallet-name-integration"
												className="h-[38px] flex-1"
											/>
											<Button
												type="button"
												variant="subtle"
												size="sm"
												onClick={handleSlackChannelSave}
											>
												Save
											</Button>
										</div>
									) : (
										<div className="flex items-center justify-between rounded-xl border border-[#00E6C3]/35 bg-[#00E6C3]/5 px-4 py-3">
											<div className="flex items-center gap-2">
												<CheckCircle
													size={16}
													className="shrink-0 text-[#00E6C3]"
												/>
												<span className="font-medium text-sm text-(--color-foreground)">
													Slack Channel:
												</span>
												<span className="text-(--color-muted-foreground) text-sm">
													{wallet.slack_channel}
												</span>
											</div>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={handleDeleteSlackChannel}
												className="text-(--color-destructive) hover:text-(--color-destructive)"
												title="Delete Slack channel"
											>
												<Trash2 size={16} />
											</Button>
										</div>
									)}
								</div>
							</>
						)}

						<div className="space-y-2">
							<ChecklistItemComponent
								item={toChecklistItem(
									"integration_completed",
									wallet.integration_completed,
								)}
								onToggle={() =>
									handleChecklistToggle(
										"integration_completed",
										wallet.integration_completed,
									)
								}
								disabled={isUpdating}
							/>
							<p className="text-(--color-muted-foreground) text-xs pl-1">
								✓ When checked, status will move to "Testing"
							</p>
						</div>
					</>
				)}

				{/* STEP 3: Testing */}
				{viewingStep === "Testing" && (
					<>
						<div>
							<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
								Step 3: Testing Phase
							</h3>
							<p className="text-(--color-muted-foreground) text-sm">
								{isPartnerView
									? "Complete the required test cases and submit results for certification."
									: "Review partner test results and certify them."}
							</p>
						</div>

						{isPartnerView ? (
							// Partner View - Can submit test results
							<TestingSection
								wallet={wallet}
								isUpdating={isUpdating}
								onSubmitResult={(result) => {
									// Check if this is an update or new submission
									const existingIndex = wallet.test_results?.findIndex(
										(tr) => tr.id === result.id,
									);

									let updatedResults: typeof wallet.test_results =
										wallet.test_results;
									if (existingIndex !== undefined && existingIndex >= 0) {
										// Update existing result
										updatedResults = wallet.test_results?.map((tr) =>
											tr.id === result.id ? result : tr,
										);
									} else {
										// Add new result
										updatedResults = [...(wallet.test_results || []), result];
									}

									updateWallet(wallet.id, {
										testResults: updatedResults,
									});

									onShowSuccess?.(
										"Test results submitted successfully! Awaiting certification from MoneyGram team.",
									);
								}}
							/>
						) : (
							// Admin View - Can view and certify results
							<TestingAdminView
								wallet={wallet}
								isUpdating={isUpdating}
								onCertify={(testResultId, adminNotes) => {
									const updatedResults = wallet.test_results?.map((tr) =>
										tr.id === testResultId
											? {
													...tr,
													status: "certified" as const,
													certifiedBy: user?.id,
													certifiedAt: new Date().toISOString(),
													adminNotes,
												}
											: tr,
									);

									// Derive product type from wallet products
									const getProductType = (): ProductType | undefined => {
										if (!wallet.products || wallet.products.length === 0)
											return undefined;

										const hasDeposit = wallet.products.includes("Deposit");
										const hasWithdraw = wallet.products.includes("Withdraw");

										if (hasDeposit && hasWithdraw) return "both";
										if (hasDeposit) return "deposit";
										if (hasWithdraw) return "withdraw";
										return undefined;
									};

									const productType = getProductType();
									const applicableTestCases = productType
										? getApplicableTestCases(productType)
										: [];

									const allCertified = applicableTestCases.every((tc) =>
										updatedResults?.some(
											(tr) =>
												tr.test_case_id === tc.id && tr.status === "certified",
										),
									);

									// Update wallet with certified result and mark testing complete if all are done
									updateWallet(wallet.id, {
										test_results: updatedResults,
										testing_completed: allCertified
											? {
													...wallet.testing_completed,
													completed: true,
													completed_at: new Date().toISOString(),
												}
											: wallet.testing_completed,
									});

									if (allCertified) {
										onShowSuccess?.(
											"Test certified successfully! All tests are now certified - Testing phase is complete.",
										);
									} else {
										onShowSuccess?.("Test certified successfully!");
									}
								}}
							/>
						)}

						<div className="space-y-2 mt-3">
							<ChecklistItemComponent
								item={{
									...toChecklistItem(
										"testing_completed",
										wallet.testing_completed,
									),
									completed:
										wallet.testing_completed?.completed ||
										(() => {
											// Auto-check if all tests are certified
											const getProductType = (): ProductType | undefined => {
												if (!wallet.products || wallet.products.length === 0)
													return undefined;
												const hasDeposit = wallet.products.includes("Deposit");
												const hasWithdraw =
													wallet.products.includes("Withdraw");
												if (hasDeposit && hasWithdraw) return "both";
												if (hasDeposit) return "deposit";
												if (hasWithdraw) return "withdraw";
												return undefined;
											};
											const productType = getProductType();
											if (!productType) return false;
											const applicableTestCases =
												getApplicableTestCases(productType);
											return applicableTestCases.every((tc) =>
												wallet.test_results?.some(
													(tr) =>
														tr.test_case_id === tc.id &&
														tr.status === "certified",
												),
											);
										})(),
								}}
								onToggle={() => {
									// Disabled - can only be toggled by certifying all tests
									notifications.error(
										"Testing cannot be manually completed. All test cases must be certified first.",
									);
								}}
								disabled={true}
							/>
							<p className="text-(--color-muted-foreground) text-xs pl-1">
								✓ When checked, status will move to "Ready to Go Live"
							</p>
						</div>
					</>
				)}

				{/* STEP 4: Ready to Go Live */}
				{viewingStep === "Ready to Go Live" && (
					<>
						<div>
							<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
								Step 4: Production Deployment
							</h3>
							<p className="text-(--color-muted-foreground) text-sm">
								{isPartnerView
									? "You are almost ready for production. Add the required production information."
									: "Partner is ready for production. Complete final steps before going live."}
							</p>
						</div>

						<div>
							<h4 className="text-sm font-semibold text-(--color-foreground) mb-1">
								Production Whitelisting
							</h4>
							{!isPartnerView && (
								<>
									<p className="mb-3 text-(--color-muted-foreground) text-xs">
										Collect production whitelisting information and create a
										ServiceNow ITSR ticket to whitelist in Production.
									</p>

									<div className="mb-3">
										<ol style={{ paddingLeft: "20px", margin: 0 }}>
											<li style={{ marginBottom: "8px" }}>
												Manage production whitelisting information
											</li>
											<li style={{ marginBottom: "8px" }}>
												Create an ITSR (IT Service Request) in ServiceNow
											</li>
											<li style={{ marginBottom: "8px" }}>
												Include all whitelisting information in the ticket
											</li>
											<li>Once whitelisted, check the task below</li>
										</ol>
									</div>
								</>
							)}

							{/* Production Information Summary */}
							<div className="mb-5 rounded-xl bg-(--color-muted) p-4">
								<div className="mb-3 flex items-center justify-between">
									<h5 className="m-0 font-semibold text-[0.938rem]">
										Production Information
									</h5>
									<Button
										type="button"
										variant="subtle"
										size="sm"
										onClick={() => setShowProductionForm(true)}
									>
										<Edit size={14} />
										{wallet.production_home_domain ? "Edit" : "Add"} Info
									</Button>
								</div>

								{wallet.production_home_domain ||
								wallet.production_funding_address ||
								wallet.production_deposit_address ||
								wallet.production_auth_address ||
								wallet.production_toml_file_domain ? (
									<div className="grid grid-cols-2 gap-3 text-sm">
										{wallet.production_custodial_type && (
											<div className="col-span-2">
												<strong>Custodial Type:</strong>{" "}
												<span className="text-(--color-muted-foreground)">
													{wallet.production_custodial_type}
												</span>
											</div>
										)}
										{wallet.production_toml_file_domain && (
											<div className="col-span-2">
												<strong>TOML File Domain:</strong>
												<br />
												<span className="text-(--color-muted-foreground)">
													{wallet.production_toml_file_domain}
												</span>
											</div>
										)}
										{wallet.production_home_domain && (
											<div>
												<strong>Home Domain:</strong>
												<br />
												<span className="text-(--color-muted-foreground)">
													{wallet.production_home_domain}
												</span>
											</div>
										)}
										{wallet.production_funding_address && (
											<div>
												<strong>Funding Address:</strong>
												<br />
												<span className="break-all text-(--color-muted-foreground)">
													{wallet.production_funding_address}
												</span>
											</div>
										)}
										{wallet.production_deposit_address && (
											<div>
												<strong>Deposit Address:</strong>
												<br />
												<span className="break-all text-(--color-muted-foreground)">
													{wallet.production_deposit_address}
												</span>
											</div>
										)}
										{wallet.production_auth_address && (
											<div>
												<strong>Auth Address:</strong>
												<br />
												<span className="break-all text-(--color-muted-foreground)">
													{wallet.production_auth_address}
												</span>
											</div>
										)}
										{wallet.excluded_countries &&
											wallet.excluded_countries.length > 0 && (
												<div style={{ gridColumn: "1 / -1" }}>
													<strong>Excluded Countries:</strong>
													<br />
													<div
														style={{
															display: "flex",
															flexWrap: "wrap",
															gap: "6px",
															marginTop: "6px",
														}}
													>
														{wallet.excluded_countries.map((country) => (
															<span
																key={country}
																className="rounded-full bg-(--color-destructive-subtle) px-2 py-0.5 text-(--color-destructive) text-[0.813rem]"
															>
																{country}
															</span>
														))}
													</div>
												</div>
											)}
									</div>
								) : (
									<p className="m-0 text-(--color-muted-foreground) text-sm">
										No production information added yet. Click "Add Info" to get
										started.
									</p>
								)}
							</div>

							{!isPartnerView && (
								<>
									<Button
										type="button"
										variant="subtle"
										size="sm"
										onClick={showProductionWhitelistInfo}
										className="mb-5"
									>
										View Full Whitelisting Info
									</Button>

									{hasRequiredProductionInfo() ? (
										<ChecklistItemComponent
											item={toChecklistItem(
												"whitelisted_in_production",
												wallet.whitelisted_in_production,
											)}
											onToggle={() =>
												handleChecklistToggle(
													"whitelisted_in_production",
													wallet.whitelisted_in_production,
												)
											}
											disabled={isUpdating}
										/>
									) : (
										<Alert variant="warning" className="mt-4">
											<WarningIcon size={20} />
											<AlertTitle>
												Please complete all required production information
												before checking this task.
											</AlertTitle>
											<AlertDescription>
												<ul className="mt-2 list-disc pl-5">
													{!wallet.agent_id && <li>Agent ID is required</li>}
													{!wallet.main_office_id && (
														<li>Main Office ID is required</li>
													)}
													{(wallet.production_custodial_type ||
														wallet.custodial_type) === "Non-Custodial" &&
														!wallet.production_toml_file_domain && (
															<li>
																TOML File Domain is required for Non-Custodial
																wallets
															</li>
														)}
													{(wallet.production_custodial_type ||
														wallet.custodial_type) === "Custodial" && (
														<>
															{!wallet.production_home_domain && (
																<li>
																	Home Domain is required for Custodial wallets
																</li>
															)}
															{!wallet.production_funding_address && (
																<li>
																	Funding Address is required for Custodial
																	wallets
																</li>
															)}
															{!wallet.production_deposit_address && (
																<li>
																	Deposit Address is required for Custodial
																	wallets
																</li>
															)}
															{!wallet.production_auth_address && (
																<li>
																	Authentication Address is required for
																	Custodial wallets
																</li>
															)}
														</>
													)}
													{(wallet.production_custodial_type ||
														wallet.custodial_type) === "Both" && (
														<>
															{!wallet.production_toml_file_domain && (
																<li>TOML File Domain is required</li>
															)}
															{!wallet.production_home_domain && (
																<li>Home Domain is required</li>
															)}
															{!wallet.production_funding_address && (
																<li>Funding Address is required</li>
															)}
															{!wallet.production_deposit_address && (
																<li>Deposit Address is required</li>
															)}
															{!wallet.production_auth_address && (
																<li>Authentication Address is required</li>
															)}
														</>
													)}
												</ul>
											</AlertDescription>
										</Alert>
									)}
								</>
							)}

							{/* Production Whitelist Status for Partner View */}
							{isPartnerView && (
								<div
									className={`mt-5 rounded-xl border px-5 py-4 ${wallet.whitelisted_in_production?.completed ? "border-[#00E6C3]/35 bg-[#00E6C3]/10" : "border-[#F59E0B]/35 bg-[#F59E0B]/10"}`}
								>
									{wallet.whitelisted_in_production?.completed ? (
										<div className="flex items-center gap-2">
											<CheckCircle size={20} className="text-[#00E6C3]" />
											<div>
												<strong className="text-(--color-foreground)">
													Wallet is Whitelisted in Production
												</strong>
												<p className="mt-1 mb-0 text-(--color-muted-foreground) text-sm">
													You can now add verification transactions below.
												</p>
											</div>
										</div>
									) : (
										<div className="flex items-center gap-2">
											<Clock size={20} className="text-[#F59E0B]" />
											<div>
												<strong className="text-(--color-foreground)">
													Waiting for Production Whitelisting
												</strong>
												<p className="mt-1 mb-0 text-(--color-muted-foreground) text-sm">
													MoneyGram admin needs to whitelist your wallet in
													production before you can add transactions.
												</p>
											</div>
										</div>
									)}
								</div>
							)}
						</div>

						<ProductionWhitelistForm
							wallet={wallet}
							isOpen={showProductionForm}
							onClose={() => setShowProductionForm(false)}
							onSave={(data) => updateWallet(wallet.id, data)}
						/>

						<div className="rounded-xl border border-(--color-border) bg-(--color-muted) p-5 mt-6">
							<h4>Go Live Verification</h4>
							<p
								className="text-(--color-muted-foreground) text-sm"
								style={{ marginBottom: "20px" }}
							>
								Record a successful transaction to verify the wallet is working
								in production.
							</p>

							{wallet.onboarding_status !== "Onboarded" && (
								<div className="mb-4 flex items-center gap-2.5 rounded-md border border-(--color-warning) bg-(--color-warning-light) px-4 py-3 text-(--color-warning-dark) text-sm">
									<WarningIcon size={18} />
									<span>Complete onboarding before adding transactions</span>
								</div>
							)}

							{/* Add Transaction Form */}
							<div className="mb-6">
								{!showAddTransaction ? (
									<Button
										type="button"
										variant="subtle"
										size="sm"
										onClick={() => setShowAddTransaction(true)}
										disabled={
											wallet.onboarding_status !== "Onboarded" ||
											!wallet.whitelisted_in_production?.completed
										}
										title={
											wallet.onboarding_status !== "Onboarded"
												? "Complete onboarding first"
												: !wallet.whitelisted_in_production?.completed
													? "Wallet must be whitelisted in production first"
													: ""
										}
									>
										<Plus size={16} />
										Add Transaction
									</Button>
								) : (
									<div className="rounded-xl bg-(--color-muted) p-5">
										<div className="flex items-start gap-3">
											<select
												value={transactionType}
												onChange={(e) =>
													setTransactionType(
														e.target.value as "Deposit" | "Withdraw",
													)
												}
												className="h-[38px] min-w-[140px] rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-[0.938rem] text-(--color-foreground)"
											>
												<option value="Deposit">Deposit</option>
												<option value="Withdraw">Withdraw</option>
											</select>
											<Input
												value={transactionRef}
												onChange={(e) => setTransactionRef(e.target.value)}
												placeholder="Transaction Reference Number"
												className="h-[38px] flex-1"
											/>
											<div className="flex gap-2">
												<Button
													type="button"
													variant="default"
													onClick={handleAddTransaction}
												>
													Save
												</Button>
												<Button
													type="button"
													variant="outline"
													onClick={() => setShowAddTransaction(false)}
												>
													Cancel
												</Button>
											</div>
										</div>
									</div>
								)}
							</div>

							{/* Transaction List */}
							{(wallet.transactions ?? []).length > 0 && (
								<div className="mb-6">
									<h5 className="mb-3 font-semibold text-[0.938rem]">
										Recorded Transactions
									</h5>
									<div className="flex flex-col gap-2">
										{(wallet.transactions ?? []).map((tx) => (
											<div
												key={tx.id}
												className="flex items-center justify-between rounded-xl border border-(--color-border) bg-(--color-card) px-4 py-3"
											>
												<div className="flex flex-1 items-center gap-4">
													<span
														className={`min-w-[80px] font-semibold ${tx.type === "Deposit" ? "text-(--color-success)" : "text-(--color-warning)"}`}
													>
														{tx.type}
													</span>
													<span className="flex-1 text-(--color-muted-foreground)">
														Ref: {tx.referenceNumber}
													</span>
													<span className="text-(--color-muted-foreground) text-sm">
														{new Date(tx.timestamp).toLocaleString()}
													</span>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													onClick={() => deleteTransaction(wallet.id, tx.id)}
													className="ml-3 text-(--color-destructive) hover:text-(--color-destructive)"
													title="Delete transaction"
												>
													<Trash2 size={18} />
												</Button>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Go Live Verification Checkbox */}
							{(wallet.transactions ?? []).length > 0 &&
							wallet.onboarding_status === "Onboarded" ? (
								<div style={{ marginBottom: "16px" }}>
									<ChecklistItemComponent
										item={toChecklistItem(
											"go_live_verification",
											wallet.go_live_verification,
										)}
										onToggle={() =>
											handleChecklistToggle(
												"go_live_verification",
												wallet.go_live_verification,
											)
										}
										disabled={isUpdating}
									/>
								</div>
							) : (
								<div className="mb-4 rounded-xl bg-(--color-muted) p-4">
									<div className="flex items-center gap-3 opacity-50">
										<Circle size={20} />
										<span style={{ fontWeight: 500 }}>
											Go Live Verification
										</span>
									</div>
									<p className="mt-2 mb-0 ml-8 text-(--color-muted-foreground) text-sm">
										{(wallet.transactions ?? []).length === 0
											? "Add at least one transaction before checking this task"
											: wallet.onboarding_status !== "Onboarded"
												? "Complete onboarding before checking this task"
												: "Requirements not met"}
									</p>
								</div>
							)}

							<p className="text-(--color-muted-foreground) text-xs pl-1">
								✓ When both tasks above are complete (plus onboarding), wallet
								will be marked as "Live"
							</p>
						</div>
					</>
				)}

				{/* Live Status */}
				{viewingStep === "Live" && (
					<>
						<div className="success-message">
							<div className="mb-3 flex items-center gap-3">
								<CelebrateIcon size={32} />
								<h3 className="m-0 font-semibold text-lg text-(--color-foreground)">
									Wallet is Live!
								</h3>
							</div>
							<p className="text-(--color-muted-foreground)">
								Integration and onboarding are complete. Wallet is now active in
								production.
							</p>
						</div>
					</>
				)}

				{/* On-Hold/Abandoned */}
				{(viewingStep === "On-Hold" || viewingStep === "Abandoned") && (
					<div>
						<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
							{wallet.integration_status}
						</h3>
						<p className="text-(--color-muted-foreground) text-sm">
							This integration is currently{" "}
							{wallet.integration_status.toLowerCase()}. Update the status above
							to resume.
						</p>
					</div>
				)}
			</div>
		</>
	);
};

export default IntegrationWorkflow;
